import { API_URL } from '../../../../constants/urls.constants';
import type { GetQueryArgsType } from '../../../../types/global.types';
import { appServiceApi } from '../../../apis/app-service.api';
import { APP_API_TAGS } from '../../../app.constants';
import type {
  GetAllOvertimePoliciesParams,
  GetAllOvertimePoliciesResponse,
  GetAllSimplifiedPoliciesParams,
  GetAllSimplifiedPoliciesResponse,
  GetSingleLeavePolicyResponse,
  GetSingleOvertimePolicyResponse,
} from './policies.type';

const { POLICIES } = APP_API_TAGS || {};

export const policiesApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ------ Start: API Endpoints for Leave Policy Management ------
    createLeavePolicy: builder.mutation({
      query: (data) => ({
        url: API_URL.policies,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [POLICIES],
    }),

    createLeavePolicyWithMultiOffice: builder.mutation({
      query: (data) => ({
        url: `${API_URL.policies}/bulk`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [POLICIES],
    }),

    updateLeavePolicy: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.policies}/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (res, __, arg) => {
        return res ? [{ type: POLICIES, id: arg.id }, POLICIES] : [];
      },
    }),

    getAllSimplifiedPolicies: builder.query<
      GetAllSimplifiedPoliciesResponse,
      GetQueryArgsType<GetAllSimplifiedPoliciesParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.policies}/list/simplified`,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [POLICIES],
    }),

    getSingleLeavePolicy: builder.query<GetSingleLeavePolicyResponse, string>({
      query: (policyId) => ({
        url: `${API_URL.policies}/${policyId}`,
        method: 'GET',
      }),
      providesTags: (result, _, policyId) =>
        result ? [{ type: POLICIES, id: policyId }] : [],
    }),
    // ------ END: API Endpoints for Leave Policy Management ------

    // ------ START: API Endpoints for Overtime Policy Management ------
    createOvertimePolicy: builder.mutation({
      query: (data) => ({
        url: API_URL.overtimePolicy,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [POLICIES],
    }),

    updateOvertimePolicy: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.overtimePolicy}/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (res, __, arg) => {
        return res ? [{ type: POLICIES, id: arg.id }, POLICIES] : [];
      },
    }),

    getAllOvertimePolicies: builder.query<
      GetAllOvertimePoliciesResponse,
      GetQueryArgsType<GetAllOvertimePoliciesParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.overtimePolicy,
        method: 'GET',
        ...queryArgs,
      }),
      providesTags: [POLICIES],
    }),

    getSingleOvertimePolicy: builder.query<
      GetSingleOvertimePolicyResponse,
      string
    >({
      query: (policyId) => ({
        url: `${API_URL.overtimePolicy}/${policyId}`,
        method: 'GET',
      }),
      providesTags: (result, _, policyId) =>
        result ? [{ type: POLICIES, id: policyId }] : [],
    }),
    // ------ END: API Endpoints for Overtime Policy Management ------
  }),
});

// Export policies API hooks
export const {
  useCreateLeavePolicyMutation,
  useCreateLeavePolicyWithMultiOfficeMutation,
  useUpdateLeavePolicyMutation,
  useGetAllSimplifiedPoliciesQuery,
  useGetSingleLeavePolicyQuery,
  useCreateOvertimePolicyMutation,
  useUpdateOvertimePolicyMutation,
  useGetAllOvertimePoliciesQuery,
  useGetSingleOvertimePolicyQuery,
} = policiesApi;
